import { combineEpics } from 'redux-observable'
import { concat, from, of, throwError, timer } from 'rxjs'
import { catchError, delayWhen, filter, map, mergeMap, switchMap } from 'rxjs/operators'
import { isStatusOk } from '@/api/helpers'
import { closeModal } from '@/components/composed/Modal/actions'
import { getLocation, getLocationQuery } from '@/logic/app/reducer'
import type { Epic } from '@/logic/interfaces'
import { fetchAgrarianOrder } from '@/logic/order/actions'
import { requestFailed, requestFinished, requestStarted } from '@/logic/requestStatus/actions'
import { OrderDocumentsService, OrganizationDocumentsService, SaleRequestDocumentsService } from '@/services/documents'
import { DEFAULT_LIMIT_DOCUMENTS, DEFAULT_OFFSET_DOCUMENTS } from '@/services/documents/constants'
import type { IFetchOrganizationDocumentOptions } from '@/services/documents/OrganizationDocumentsService'
import type { IFetchDocumentOptions } from '@/services/documents/SaleRequestDocumentsService'
import type { IDocument } from '@/types/Documents'
import { EDocumentEntityType } from '@/types/Documents'
import type { IUpdateDocumentPayload, IUploadDocumentPayload } from './components/UploadDocumentModal/interfaces'
import {
  deleteDocument,
  downloadDocument,
  fetchDocuments,
  reloadDocuments,
  setDocuments,
  setPending,
  updateDocument,
  uploadDocument,
} from './actions'
import { EntityVersionGetter } from './helpers'
import type { IFetchDocumentsPayload, IFileDownloadPayload } from './interfaces'

const DocumentsServices = {
  [EDocumentEntityType.MY_DOCUMENTS]: new OrganizationDocumentsService(),
  [EDocumentEntityType.ORDERS]: new OrderDocumentsService(),
  [EDocumentEntityType.REQUESTS]: new SaleRequestDocumentsService(false),
  [EDocumentEntityType.MULTIREQUESTS]: new SaleRequestDocumentsService(true),
} as const

const getDocumentService = (entityType: EDocumentEntityType) => {
  return DocumentsServices[entityType]
}

const onUploadDocumentDoMakeRequest: Epic<IUploadDocumentPayload, typeof uploadDocument> = (action$, state$) =>
  action$.pipe(
    filter(uploadDocument.match),
    mergeMap(({ payload }) => {
      const state = state$.value
      const { documentFile, documentName, documentType, entity, entityId } = payload
      const requestStatus = { name: `uploadDocument_${documentType}` }
      const version = EntityVersionGetter[entity]?.(state)

      if (!entityId) return of(requestFailed(requestStatus))

      const service = getDocumentService(entity)

      return concat(
        of(requestStarted(requestStatus)),
        from(
          service.uploadDocument({
            id: entityId,
            file: documentFile,
            version,
            name: documentName,
            type: documentType,
          }),
        ).pipe(
          mergeMap(response => {
            if (!isStatusOk(response)) return throwError(response)

            return [
              requestFinished(requestStatus),
              closeModal(),
              fetchDocuments({
                entity,
                entityId,
                commonTail:
                  entity === EDocumentEntityType.REQUESTS ||
                  entity === EDocumentEntityType.MULTIREQUESTS ||
                  entity === EDocumentEntityType.ORDERS,
              }),
            ]
          }),
          catchError(() => [closeModal(), requestFailed(requestStatus)]),
        ),
      )
    }),
  )

const onUpdateDocumentDoMakeRequest: Epic<IUpdateDocumentPayload, typeof updateDocument> = (action$, state$) =>
  action$.pipe(
    filter(updateDocument.match),
    mergeMap(({ payload }) => {
      const state = state$.value
      const { documentFile, documentName, document, entityId, entity, orderId } = payload
      const { type, id } = document
      const requestStatus = { name: `updateDocument_${type}` }
      const version = EntityVersionGetter[entity]?.(state)

      if (!entityId) return of(requestFailed(requestStatus))

      const service = getDocumentService(entity)

      return concat(
        of(requestStarted(requestStatus)),
        from(
          service.updateDocument({
            documentId: id,
            id: entityId,
            file: documentFile,
            version,
            name: documentName,
            type,
          }),
        ).pipe(
          mergeMap(response => {
            if (!isStatusOk(response)) return throwError(response)

            return [
              requestFinished(requestStatus),
              closeModal(),
              fetchDocuments({
                entityId,
                entity,
                commonTail:
                  entity === EDocumentEntityType.REQUESTS ||
                  entity === EDocumentEntityType.MULTIREQUESTS ||
                  entity === EDocumentEntityType.ORDERS,
              }),
              orderId ? fetchAgrarianOrder({ id: orderId, shouldNotShowLoadingState: true }) : undefined,
            ]
          }),
          catchError(() => [closeModal(), requestFailed(requestStatus)]),
        ),
      )
    }),
  )

const onDownloadDocumentDoMakeRequest: Epic<IFileDownloadPayload, typeof downloadDocument> = action$ =>
  action$.pipe(
    filter(downloadDocument.match),
    mergeMap(({ payload }) => {
      const { url, fileName, entity, id } = payload
      const requestStatus = { name: `downloadDocument_${id}` }

      const service = getDocumentService(entity)

      return concat(
        of(requestStarted(requestStatus)),
        from(service.downloadDocument(url, fileName)).pipe(
          map(() => requestFinished(requestStatus)),
          catchError(() => of(requestFailed(requestStatus))),
        ),
      )
    }),
  )

const onFetchDocumentsDoMakeRequest: Epic<IFetchDocumentsPayload, typeof fetchDocuments> = action$ =>
  action$.pipe(
    filter(fetchDocuments.match),
    mergeMap(({ payload }) => {
      const { entity, entityId, offset = DEFAULT_OFFSET_DOCUMENTS, limit = DEFAULT_LIMIT_DOCUMENTS, commonTail } = payload

      if (entity === EDocumentEntityType.MY_DOCUMENTS) {
        const fetchDocumentsPayload: IFetchOrganizationDocumentOptions = { offset, limit }

        return from(DocumentsServices[entity].fetchDocuments(fetchDocumentsPayload)).pipe(
          mergeMap(response => (isStatusOk(response) ? [setDocuments(response.data)] : throwError(null))),
          catchError(() => [setPending(false)]),
        )
      } else if (
        entity === EDocumentEntityType.REQUESTS ||
        entity === EDocumentEntityType.MULTIREQUESTS ||
        entity === EDocumentEntityType.ORDERS
      ) {
        if (entityId === undefined) {
          return of(setPending(false))
        }

        const fetchDocumentsPayload: IFetchDocumentOptions = { id: entityId, offset, limit, commonTail }
        const service = getDocumentService(entity)

        return from(service.fetchDocuments(fetchDocumentsPayload)).pipe(
          mergeMap(response => (isStatusOk(response) ? [setDocuments(response.data)] : throwError(null))),
          catchError(() => [setPending(false)]),
        )
      }

      return of(setPending(false))
    }),
  )

const onReloadDocumentsDoSetReloadingByInterval: Epic<IFetchDocumentsPayload, typeof reloadDocuments> = (action$, state$) =>
  action$.pipe(
    filter(reloadDocuments.match),
    delayWhen(() => timer(10000)),
    filter(({ payload }) => {
      const { value: state } = state$

      return (getLocation()(state$.value).pathname || '').includes(String(payload.entityId)) && getLocationQuery(state)?.tab === 'documents'
    }),
    switchMap(({ payload }) => [
      fetchDocuments({
        ...payload,
        shouldNotShowLoadingState: true,
        commonTail:
          payload.entity === EDocumentEntityType.REQUESTS ||
          payload.entity === EDocumentEntityType.MULTIREQUESTS ||
          payload.entity === EDocumentEntityType.ORDERS,
      }),
      reloadDocuments(payload),
    ]),
  )

const onDeleteDocumentDoMakeRequest: Epic<IDocument, typeof deleteDocument> = (action$, state$) =>
  action$.pipe(
    filter(deleteDocument.match),
    mergeMap(({ payload }) => {
      const state = state$.value
      const { id, entityId, entity, type } = payload
      const requestStatus = { name: `deleteDocument_${type}` }
      const version = EntityVersionGetter[entity]?.(state)

      if (!entityId) return of(requestFailed(requestStatus))

      const service = getDocumentService(entity)

      return concat(
        of(requestStarted(requestStatus)),
        from(
          service.deleteDocument({
            id: entityId,
            documentId: id,
            version,
          }),
        ).pipe(
          mergeMap(response =>
            isStatusOk(response)
              ? [
                  fetchDocuments({
                    entityId,
                    entity,
                    commonTail:
                      entity === EDocumentEntityType.REQUESTS ||
                      entity === EDocumentEntityType.MULTIREQUESTS ||
                      entity === EDocumentEntityType.ORDERS,
                  }),
                  requestFinished(requestStatus),
                ]
              : throwError(null),
          ),
          catchError(() => of(requestFailed(requestStatus))),
        ),
      )
    }),
  )

//eslint-disable-next-line @typescript-eslint/no-explicit-any
export default (<any>combineEpics)(
  onUploadDocumentDoMakeRequest,
  onUpdateDocumentDoMakeRequest,
  onFetchDocumentsDoMakeRequest,
  onDeleteDocumentDoMakeRequest,
  onDownloadDocumentDoMakeRequest,
  onReloadDocumentsDoSetReloadingByInterval,
)
